import { useRef, useState, useMemo } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { api } from "@cliqsuite/db/powersync";
import { effectiveSubscriptionStatus } from "../shell/SubscriptionGate";
import {
  businessesCollection,
  subscriptionPlansCollection,
  billingCyclesCollection,
  subscriptionPaymentsCollection,
  paymentMethodConfigsCollection,
  subscriptionDiscountsCollection,
  storesCollection,
  staffCollection,
  syncApiUrl,
  getAuthToken,
  eq,
  type SubscriptionPlan,
  type BillingCycle,
  type PaymentMethodConfig,
  type SubscriptionDiscount,
  type DiscountCode,
  isOwnerStaff as isOwnerRow,
} from "@cliqsuite/db";
import { planCyclePrice } from "@cliqsuite/db";
import { Button, colors, figure } from "@cliqsuite/ui";
import { SectionCard, EmptyState, IconButton, Pill } from "../common/Page";
import { Modal, Field, TextInput, TextArea } from "../common/Modal";
import { Toast } from "../common/Extras";
import { useAuth } from "../../lib/auth";
import { money, formatDate } from "../../lib/format";
import { MODULE_CATALOG } from "../../lib/moduleCatalog";
import { PageBanner, PageHeaderActions } from "../shell/PageHeaderActions";

const CORE_MODULES = new Set(["pos", "payments", "reports"]);

// Keeps every plan card the same height regardless of how many add-on
// modules a plan includes — past this many, the rest move into a "View
// more" dialog instead of stretching the card.
const FEATURE_DISPLAY_LIMIT = 5;

function planFeaturesOf(plan: SubscriptionPlan): { modules: string[] } {
  const f = (plan.features ?? {}) as { modules?: unknown };
  return { modules: Array.isArray(f.modules) ? f.modules.filter((m): m is string => typeof m === "string") : [] };
}

/**
 * The canvas's per-tier checklist: limits on one line, then "Everything in <the tier below>" plus
 * only what this tier adds. The lowest tier lists its own modules. Keys the catalog doesn't know
 * are left out rather than shown raw.
 */
function planFeatureList(plan: SubscriptionPlan, below?: SubscriptionPlan | null): string[] {
  const names = (p: SubscriptionPlan) =>
    planFeaturesOf(p).modules.filter((m) => !CORE_MODULES.has(m)).flatMap((k) => MODULE_CATALOG.find((m) => m.key === k)?.name ?? []);
  const stores = plan.max_stores >= 999 ? "Unlimited stores" : `${plan.max_stores} store${plan.max_stores === 1 ? "" : "s"}`;
  const seats = plan.max_staff >= 999 ? "unlimited staff seats" : `${plan.max_staff} staff seats`;
  const inBelow = new Set(below ? names(below) : []);
  return [`${stores}, ${seats}`, below ? `Everything in ${below.name}` : "Register, payments, reports", ...names(plan).filter((n) => !inBelow.has(n))];
}

const SUBSCRIPTION_STATUS_META: Record<string, { label: string; chipBg: string; chipFg: string }> = {
  active: { label: "Active", chipBg: "rgba(33,163,91,.22)", chipFg: "#7DDBA6" },
  trial: { label: "Free trial", chipBg: "rgba(255,255,255,.14)", chipFg: "#C7CBD4" },
  inactive: { label: "Inactive", chipBg: "rgba(255,255,255,.14)", chipFg: "#C7CBD4" },
  expired: { label: "Ended", chipBg: "rgba(224,68,94,.22)", chipFg: "#F3A0AD" },
  suspended: { label: "Suspended", chipBg: "rgba(224,68,94,.22)", chipFg: "#F3A0AD" },
};

const esc = (v: string) => v.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

/** Saves a paid subscription payment as a one-page invoice the owner can keep or print.
 * ponytail: a self-contained HTML file, not a PDF; switch to a server-made PDF if accounting needs one. */
function downloadInvoice(rows: [string, string][], reference: string, total: string) {
  const html = `<!doctype html><meta charset="utf-8"><title>Invoice ${esc(reference)}</title>
<style>body{font:15px/1.5 system-ui,sans-serif;color:#20242e;max-width:560px;margin:48px auto;padding:0 24px}h1{font-size:26px;margin:0}
small{color:#5f6470}table{width:100%;border-collapse:collapse;margin-top:28px}td{padding:10px 0;border-top:1px solid #e9eaee}
td:last-child{text-align:right;font-weight:700}tr.total td{font-size:18px;font-weight:800;border-top:2px solid #20242e}</style>
<h1>CliqSuite</h1><small>Invoice ${esc(reference)}, paid</small>
<table>${rows.map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`).join("")}
<tr class="total"><td>Total paid</td><td>${esc(total)}</td></tr></table>`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
  a.download = `invoice-${reference}.html`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function trialDaysLeft(end: string): string {
  const days = Math.max(0, Math.ceil((new Date(end).getTime() - Date.now()) / 86_400_000));
  return days === 1 ? "1 day left" : `${days} days left`;
}

const HISTORY_STATUS_META: Record<string, { label: string; fg: string; bg: string; icon: string; actionIcon: string; actionTitle: string }> = {
  approved: { label: "Paid", fg: "#1E8E55", bg: "#E7F6EE", icon: "receipt_long", actionIcon: "download", actionTitle: "Download invoice" },
  pending: { label: "Pending review", fg: "#8A5D00", bg: "#FCF1D4", icon: "hourglass_top", actionIcon: "visibility", actionTitle: "View submission" },
  rejected: { label: "Rejected", fg: "#C4324B", bg: "#FFF1F4", icon: "error", actionIcon: "visibility", actionTitle: "View reason" },
};

const planAmount = planCyclePrice;

function discountAmount(base: number, type: "percent" | "flat", value: number): number {
  const off = type === "percent" ? Math.round((base * value) / 100) : value;
  return Math.min(base, Math.max(0, off));
}

/**
 * Only one standing discount ever applies, even if several are active at
 * once (e.g. a global rule and a business-specific rule together) — the
 * most specific match wins, in this order: this business + this plan, this
 * business + all plans, everyone + this plan, everyone + all plans. They
 * don't stack with each other (a redeemed discount code stacks on top of
 * whichever one of these wins — see SubmitPaymentModal).
 */
function bestStandingDiscount(discounts: SubscriptionDiscount[], businessId: string, planId: string): SubscriptionDiscount | null {
  const today = new Date().toISOString().slice(0, 10);
  const active = discounts.filter((d) => d.is_active && (!d.expires_at || d.expires_at >= today));
  const rank = (d: SubscriptionDiscount): number => {
    const businessMatch = d.scope === "business" && d.business_id === businessId;
    const globalMatch = d.scope === "global";
    if (!businessMatch && !globalMatch) return -1;
    const planMatch = d.plan_id === planId;
    const allPlans = d.plan_id === null;
    if (!planMatch && !allPlans) return -1;
    if (businessMatch && planMatch) return 4;
    if (businessMatch && allPlans) return 3;
    if (globalMatch && planMatch) return 2;
    return 1; // globalMatch && allPlans
  };
  let best: SubscriptionDiscount | null = null;
  let bestRank = 0;
  for (const d of active) {
    const r = rank(d);
    if (r > bestRank) {
      best = d;
      bestRank = r;
    }
  }
  return best;
}

export function BillingTab() {
  const { user } = useAuth();
  const businessId = user?.business_id;

  const { data: businesses } = useLiveQuery(
    (q) =>
      businessId
        ? q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, businessId))
        : q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, "__none__")),
    [businessId],
  );
  const business = businesses?.[0];

  const { data: plans, isLoading: plansLoading } = useLiveQuery((q) => q.from({ p: subscriptionPlansCollection }));
  const { data: cycles } = useLiveQuery((q) => q.from({ c: billingCyclesCollection }));
  const { data: methods } = useLiveQuery((q) => q.from({ m: paymentMethodConfigsCollection }));
  const { data: discounts } = useLiveQuery((q) => q.from({ d: subscriptionDiscountsCollection }));
  const { data: payments } = useLiveQuery(
    (q) =>
      businessId
        ? q.from({ pay: subscriptionPaymentsCollection }).where(({ pay }) => eq(pay.business_id, businessId))
        : q.from({ pay: subscriptionPaymentsCollection }).where(({ pay }) => eq(pay.id, "__none__")),
    [businessId],
  );
  const { data: stores } = useLiveQuery(
    (q) =>
      businessId
        ? q.from({ s: storesCollection }).where(({ s }) => eq(s.business_id, businessId))
        : q.from({ s: storesCollection }).where(({ s }) => eq(s.id, "__none__")),
    [businessId],
  );
  const { data: staffRows } = useLiveQuery(
    (q) =>
      businessId
        ? q.from({ s: staffCollection }).where(({ s }) => eq(s.business_id, businessId))
        : q.from({ s: staffCollection }).where(({ s }) => eq(s.id, "__none__")),
    [businessId],
  );
  // The owner's register profile is not an employee: it is left out of everything under Team.
  const staff = useMemo(() => (staffRows ?? []).filter((row) => !isOwnerRow(row) && !row.deleted_at), [staffRows]);

  const activePlans = [...(plans ?? [])].filter((p) => p.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const sortedCycles = [...(cycles ?? [])].sort((a, b) => a.months - b.months);
  const enabledMethods = [...(methods ?? [])].filter((m) => m.is_enabled).sort((a, b) => a.sort_order - b.sort_order);
  const [cycleKey, setCycleKey] = useState<string | null>(null);
  const cycle = sortedCycles.find((c) => c.key === cycleKey) ?? sortedCycles[0] ?? null;

  const [payingPlan, setPayingPlan] = useState<SubscriptionPlan | null>(null);
  const [viewingFeaturesOf, setViewingFeaturesOf] = useState<SubscriptionPlan | null>(null);
  const [viewingPaymentId, setViewingPaymentId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = (m: string) => {
    setToast(m);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };

  const sortedPayments = [...(payments ?? [])].sort(
    (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime(),
  );
  const pendingPayments = sortedPayments.filter((p) => p.status === "pending");
  const hasPending = pendingPayments.length > 0;
  const viewingPayment = sortedPayments.find((p) => p.id === viewingPaymentId) ?? null;
  const mostRecent = sortedPayments[0] ?? null;
  const hasRejected = !hasPending && mostRecent?.status === "rejected";
  const lastApproved = sortedPayments.find((p) => p.status === "approved") ?? null;

  const currentPlan = business ? (activePlans.find((p) => p.id === business.subscription_plan_id) ?? null) : null;
  const statusMeta = SUBSCRIPTION_STATUS_META[business ? effectiveSubscriptionStatus(business) : "trial"] ?? SUBSCRIPTION_STATUS_META.trial!;

  const storesUsed = stores?.length ?? 0;
  const staffUsed = (staff ?? []).filter((s) => s.is_active).length;
  const installedAddOns = (business?.installed_modules ?? []).filter((m) => !CORE_MODULES.has(m));
  const planAddOnCount = currentPlan ? planFeaturesOf(currentPlan).modules.filter((m) => !CORE_MODULES.has(m)).length : 0;
  const usage = currentPlan
    ? [
        {
          icon: "storefront",
          label: "Stores",
          value: storesUsed,
          limit: currentPlan.max_stores >= 999 ? "Unlimited" : currentPlan.max_stores,
          max: currentPlan.max_stores >= 999 ? Math.max(storesUsed, 1) : currentPlan.max_stores,
          color: "#DC2D49",
        },
        {
          icon: "group",
          label: "Staff seats",
          value: staffUsed,
          limit: currentPlan.max_staff >= 999 ? "Unlimited" : currentPlan.max_staff,
          max: currentPlan.max_staff >= 999 ? Math.max(staffUsed, 1) : currentPlan.max_staff,
          color: "#2D64C8",
        },
        {
          icon: "extension",
          label: "Add-on modules",
          value: installedAddOns.length,
          limit: planAddOnCount,
          max: Math.max(1, planAddOnCount),
          color: "#8B5CC7",
        },
      ]
    : [];

  const limitOf = (n: number) => (n >= 999 ? "unlimited" : String(n));
  const statusLabel = business?.subscription_period_end && lastApproved ? `Active until ${formatDate(business.subscription_period_end)}` : statusMeta.label;
  const subStatus = business ? effectiveSubscriptionStatus(business) : "trial";
  const statusTone: "success" | "warning" | "danger" = subStatus === "active" ? "success" : subStatus === "trial" ? "warning" : "danger";
  const nextPlanPayment = business?.subscription_period_end && currentPlan ? { date: business.subscription_period_end, plan: currentPlan } : null;

  return (
    <div>
      <PageBanner>
        {hasPending ? (
          <div className="o-banner">
            <span className="o-banner__ico"><span className="ms" aria-hidden="true">hourglass_top</span></span>
            <span className="o-banner__text">
              <b>Payment submitted, awaiting review</b>
              <span>
                We check your {money(pendingPayments[0]!.amount)} payment ({pendingPayments[0]!.reference}) against the transfer, usually within a few hours.
              </span>
            </span>
            <button type="button" className="o-banner__btn" onClick={() => setViewingPaymentId(pendingPayments[0]!.id)}>
              See submission
            </button>
          </div>
        ) : hasRejected && mostRecent ? (
          <div className="o-banner o-banner--error">
            <span className="o-banner__ico"><span className="ms" aria-hidden="true">error</span></span>
            <span className="o-banner__text">
              <b>Payment rejected</b>
              <span>{mostRecent.reject_reason || "The proof couldn't be matched."}</span>
            </span>
            <button
              type="button"
              className="o-btn o-btn--ink"
              onClick={() => {
                const plan = activePlans.find((p) => p.id === mostRecent.plan_id);
                if (plan) setPayingPlan(plan);
              }}
            >
              <span className="ms" aria-hidden="true">upload</span>
              Re-submit
            </button>
          </div>
        ) : subStatus === "trial" && business?.subscription_period_end ? (
          <div className="o-banner o-banner--info">
            <span className="o-banner__ico"><span className="ms" aria-hidden="true">hourglass_top</span></span>
            <span className="o-banner__text">
              <b>You're on a free trial, {trialDaysLeft(business.subscription_period_end)}</b>
              <span>
                Everything in {currentPlan?.name ?? "your plan"} is on. Choose a plan and pay before {formatDate(business.subscription_period_end)} to stay active.
              </span>
            </span>
            <button type="button" className="o-btn o-btn--ink" onClick={() => document.getElementById("billing-plans")?.scrollIntoView({ behavior: "smooth" })}>
              <span className="ms" aria-hidden="true">add</span>
              Choose a plan
            </button>
          </div>
        ) : null}
      </PageBanner>

      {currentPlan && (
        <PageHeaderActions>
          <button type="button" className="o-btn o-btn--line" onClick={() => setPayingPlan(currentPlan)} disabled={!cycle}>
            <span className="ms" aria-hidden="true">credit_card</span>
            Payment method
          </button>
        </PageHeaderActions>
      )}
      {business && currentPlan && (
        <div className={`o-plan-now${statusTone === "danger" ? " is-ended" : ""}`}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 22, fontWeight: 800 }}>{currentPlan.name}</span>
              {statusTone === "danger" ? (
                <span style={{ fontSize: 13, fontWeight: 800, color: "var(--coral)" }}>
                  {subStatus === "expired" && business.subscription_period_end ? `Expired ${formatDate(business.subscription_period_end)}` : statusMeta.label}
                </span>
              ) : (
                <Pill label={subStatus === "active" ? statusLabel : statusMeta.label} tone={statusTone} />
              )}
            </div>
            <div className="o-row__sub" style={{ fontSize: 13.5, marginTop: 5, whiteSpace: "normal" }}>
              {money(lastApproved ? lastApproved.amount : currentPlan.base_price)} a {(cycle?.label ?? "month").toLowerCase().replace("ly", "")}
              {lastApproved ? `, last paid with ${lastApproved.payment_method_name}` : ", renew to stay active"}
            </div>
          </div>
          <div className="o-plan-now__stat">
            <span><span className="ms" aria-hidden="true">storefront</span>Stores</span>
            <strong className="num">{storesUsed} of {limitOf(currentPlan.max_stores)}</strong>
          </div>
          <div className="o-plan-now__stat">
            <span><span className="ms" aria-hidden="true">group</span>Staff seats</span>
            <strong className="num">{staffUsed} of {limitOf(currentPlan.max_staff)}</strong>
          </div>
          <div className="o-plan-now__stat">
            <span><span className="ms" aria-hidden="true">widgets</span>Modules</span>
            <strong className="num">{installedAddOns.length + CORE_MODULES.size} included</strong>
          </div>
          <button type="button" className="o-btn o-btn--coral" style={{ height: 48 }} onClick={() => setPayingPlan(currentPlan)} disabled={!cycle}>
            <span className="ms" aria-hidden="true">autorenew</span>
            Renew
          </button>
        </div>
      )}

      <div id="billing-plans" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 15, fontWeight: 800 }}>Choose a plan</span>
        {sortedCycles.length > 0 && (
          <div role="radiogroup" aria-label="Billing cycle" className="o-seg">
            {sortedCycles.map((c) => {
              const savePct = Math.round((1 - Number(c.multiplier) / c.months) * 100);
              return (
                <button type="button" role="radio" aria-checked={(cycle?.key ?? sortedCycles[0]?.key) === c.key} key={c.key} onClick={() => setCycleKey(c.key)}>
                  {c.label}
                  {savePct > 0 ? `, save ${savePct}%` : ""}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="o-plans">
        {plansLoading && <div className="o-muted-note">Loading plans…</div>}
        {activePlans.map((p, planIndex) => {
          const isCurrent = business?.subscription_plan_id === p.id;
          const amount = cycle ? planAmount(p, cycle) : p.base_price;
          const standing = businessId ? bestStandingDiscount(discounts ?? [], businessId, p.id) : null;
          const discounted = standing ? amount - discountAmount(amount, standing.discount_type, standing.value) : amount;
          const per = !cycle || cycle.months === 1 ? "month" : cycle.label.toLowerCase().replace("ly", "").replace("annual", "year");
          const features = planFeatureList(p, activePlans[planIndex - 1]);
          return (
            <div key={p.id} className={`o-plan${isCurrent ? " is-current" : ""}`}>
              {isCurrent && <span style={{ position: "absolute", top: 18, right: 18 }}><Pill label="Your plan" tone="accent" /></span>}
              <div style={{ fontSize: 18, fontWeight: 800 }}>{p.name}</div>
              <div className="num" style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.8 }}>
                {figure(money(discounted))}
                <span style={{ fontSize: 14, fontWeight: 700, opacity: 0.6 }}> / {per}</span>
              </div>
              {standing && (
                <div style={{ fontSize: 12.5, fontWeight: 800, color: isCurrent ? "#FFB3C0" : "var(--ok)" }}>
                  {standing.label || (standing.discount_type === "percent" ? `${standing.value}% off` : `${money(standing.value)} off`)}, was {money(amount)}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14, fontWeight: 700 }}>
                {features.slice(0, FEATURE_DISPLAY_LIMIT).map((f) => (
                  <div key={f} className="o-plan__feat"><span className="ms" aria-hidden="true">check</span>{f}</div>
                ))}
                {features.length > FEATURE_DISPLAY_LIMIT && (
                  <button type="button" className="o-link" style={{ color: isCurrent ? "#FFB3C0" : undefined, textAlign: "left" }} onClick={() => setViewingFeaturesOf(p)}>
                    {features.length - FEATURE_DISPLAY_LIMIT} more
                  </button>
                )}
              </div>
              <span style={{ flex: 1 }} />
              <button type="button" className={`o-btn ${isCurrent ? "o-btn--coral" : "o-btn--grey"}`} style={{ height: 48, marginTop: 6 }} onClick={() => setPayingPlan(p)} disabled={!cycle}>
                {isCurrent ? "Renew this plan" : `Switch to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <SectionCard style={{ marginTop: 18 }}>
        <div className="o-card__top">
          <div style={{ flex: 1 }}>
            <h2>Billing history</h2>
            <div className="o-card__sub">Payments you've sent</div>
          </div>
        </div>
        {nextPlanPayment && !hasPending && statusTone !== "danger" && (
          <div className="o-list-row">
            <span className="o-ico"><span className="ms" aria-hidden="true">event_upcoming</span></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="o-row__title">Next payment, {formatDate(nextPlanPayment.date)}</div>
              <div className="o-row__sub">
                {nextPlanPayment.plan.name}, {(cycle?.label ?? "monthly").toLowerCase()}
              </div>
            </div>
            <span className="num" style={{ fontWeight: 800 }}>{money(cycle ? planAmount(nextPlanPayment.plan, cycle) : nextPlanPayment.plan.base_price)}</span>
            <Pill label="Upcoming" />
          </div>
        )}
        {sortedPayments.length === 0 && !nextPlanPayment && <div className="o-muted-note">No payments yet.</div>}
        {sortedPayments.map((pay) => {
          const plan = plans?.find((p) => p.id === pay.plan_id);
          const meta = HISTORY_STATUS_META[pay.status] ?? HISTORY_STATUS_META.approved!;
          const tone = pay.status === "approved" ? "success" : pay.status === "pending" ? "warning" : "danger";
          return (
            <div key={pay.id} className="o-list-row">
              <span className={`o-ico ${tone === "success" ? "o-ico--ok" : tone === "warning" ? "o-ico--warn" : "o-ico--soft"}`}>
                <span className="ms" aria-hidden="true">{meta.icon}</span>
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="o-row__title">
                  {formatDate(pay.submitted_at)}, {pay.payment_method_name}
                </div>
                <div className="o-row__sub">
                  {pay.reference}, {plan?.name ?? "Plan"}
                </div>
              </div>
              <span className="num" style={{ fontWeight: 800 }}>{money(pay.amount)}</span>
              <Pill label={meta.label} tone={tone} />
              {pay.status === "approved" ? (
                <button
                  type="button"
                  className="o-icon-btn"
                  aria-label={`Download invoice ${pay.reference}`}
                  onClick={() =>
                    downloadInvoice(
                      [
                        ["Billed to", business?.name ?? ""],
                        ["Plan", plan?.name ?? "Plan"],
                        ["Period", pay.period_start && pay.period_end ? `${formatDate(pay.period_start)} to ${formatDate(pay.period_end)}` : ""],
                        ["Paid with", pay.payment_method_name],
                        ["Paid on", formatDate(pay.reviewed_at ?? pay.submitted_at)],
                        ["Discount", pay.discount ? money(pay.discount) : ""],
                      ].filter((r): r is [string, string] => !!r[1]),
                      pay.reference,
                      money(pay.amount),
                    )
                  }
                >
                  <span className="ms" aria-hidden="true">download</span>
                </button>
              ) : (
                <button type="button" className="o-btn o-btn--line" style={{ height: 36 }} onClick={() => setViewingPaymentId(pay.id)}>
                  {pay.status === "rejected" ? "View reason" : "View"}
                </button>
              )}
            </div>
          );
        })}
      </SectionCard>

      {payingPlan && cycle && (
        <SubmitPaymentModal
          plan={payingPlan}
          cycle={cycle}
          methods={enabledMethods}
          businessId={businessId ?? ""}
          userId={user?.id ?? null}
          standingDiscount={businessId ? bestStandingDiscount(discounts ?? [], businessId, payingPlan.id) : null}
          onClose={() => setPayingPlan(null)}
        />
      )}

      {viewingPayment && (
        <Modal
          title={viewingPayment.status === "rejected" ? "Payment rejected" : "Your submission"}
          onClose={() => setViewingPaymentId(null)}
          footer={
            <Button variant="secondary" onClick={() => setViewingPaymentId(null)}>
              Close
            </Button>
          }
        >
          {viewingPayment.status === "rejected" && (
            <div className="o-banner o-banner--error" style={{ marginBottom: 14 }}>
              <span className="ms" aria-hidden="true">error</span>
              <span style={{ flex: 1 }}>{viewingPayment.reject_reason || "The proof couldn't be matched."}</span>
            </div>
          )}
          {(
            [
              ["Reference", viewingPayment.reference],
              ["Plan", activePlans.find((p) => p.id === viewingPayment.plan_id)?.name ?? "Plan"],
              ["Amount", money(viewingPayment.amount)],
              ["Paid with", viewingPayment.payment_method_name],
              ["Transaction ID", viewingPayment.txid],
              ["Note", viewingPayment.notes],
              ["Submitted", formatDate(viewingPayment.submitted_at)],
            ] as const
          )
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k} className="o-list-row" style={{ justifyContent: "space-between", gap: 16 }}>
                <span className="o-row__sub">{k}</span>
                <span style={{ fontWeight: 800, textAlign: "right" }}>{v}</span>
              </div>
            ))}
        </Modal>
      )}

      {viewingFeaturesOf && (
        <Modal
          title={`${viewingFeaturesOf.name} includes`}
          onClose={() => setViewingFeaturesOf(null)}
          footer={
            <Button variant="secondary" onClick={() => setViewingFeaturesOf(null)}>
              Close
            </Button>
          }
        >
          {planFeatureList(viewingFeaturesOf, activePlans[activePlans.findIndex((p) => p.id === viewingFeaturesOf.id) - 1]).map((f) => (
            <div key={f} className="o-plan__feat" style={{ padding: "6px 0", fontSize: 14, fontWeight: 700 }}>
              <span className="ms" aria-hidden="true" style={{ color: "var(--ok)" }}>check</span>
              {f}
            </div>
          ))}
        </Modal>
      )}

      <Toast message={toast} />
    </div>
  );
}

const TYPE_META: Record<PaymentMethodConfig["type"], { icon: string; iconBg: string; iconColor: string; name: string; flowTitle: string }> = {
  qr: { icon: "qr_code_2", iconBg: "#EAF0FB", iconColor: "#2D64C8", name: "QR Payment", flowTitle: "Pay by QR" },
  bank: { icon: "account_balance", iconBg: "#F3ECFB", iconColor: "#8B5CC7", name: "Bank Transfer", flowTitle: "Pay by bank transfer" },
  wallet: { icon: "currency_bitcoin", iconBg: "#E7F6EE", iconColor: "#1E8E55", name: "Crypto Wallet", flowTitle: "Pay with crypto" },
};

function copyToClipboard(text: string) {
  try {
    void navigator.clipboard?.writeText(text);
  } catch {
    // ignore
  }
}

/** Matches the mockup's 2-step wizard: pick a method type, then pay via its real QR image / bank details / wallet address before attaching proof. */
function SubmitPaymentModal({
  plan,
  cycle,
  methods,
  businessId,
  userId,
  standingDiscount,
  onClose,
}: {
  plan: SubscriptionPlan;
  cycle: BillingCycle;
  methods: PaymentMethodConfig[];
  businessId: string;
  userId: string | null;
  standingDiscount: SubscriptionDiscount | null;
  onClose: () => void;
}) {
  const [step, setStep] = useState<"method" | "pay">("method");
  const [methodType, setMethodType] = useState<PaymentMethodConfig["type"] | null>(null);
  const [subMethodId, setSubMethodId] = useState("");
  const [txid, setTxid] = useState("");
  const [notes, setNotes] = useState("");
  // proofKey is what gets persisted as proof_data (a private-bucket object
  // key, not a directly-loadable URL — payment-proofs is no longer
  // public-read, see services/sync-api/src/lib/storage.ts); proofPreviewUrl
  // is a short-lived signed URL fetched right after upload, just to show
  // this modal's own "you just attached this" preview.
  const [proofKey, setProofKey] = useState<string | null>(null);
  const [proofPreviewUrl, setProofPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState<DiscountCode | null>(null);
  const [codeError, setCodeError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const baseAmount = planAmount(plan, cycle);
  const standingOff = standingDiscount ? discountAmount(baseAmount, standingDiscount.discount_type, standingDiscount.value) : 0;
  const codeOff = appliedCode ? discountAmount(baseAmount, appliedCode.discount_type, appliedCode.value) : 0;
  const totalOff = Math.min(baseAmount, standingOff + codeOff);
  const amount = baseAmount - totalOff;

  const applyCode = async () => {
    const raw = codeInput.trim().toUpperCase();
    if (!raw) return;
    const today = new Date().toISOString().slice(0, 10);
    const match = await api<{ row: DiscountCode }>(`/platform/discount-codes/lookup/${encodeURIComponent(raw)}`)
      .then((b) => b.row)
      .catch(() => null);
    if (!match) {
      setCodeError("Code not found.");
      return;
    }
    if (!match.is_active) {
      setCodeError("This code is no longer active.");
      return;
    }
    if (match.expires_at && match.expires_at < today) {
      setCodeError("This code has expired.");
      return;
    }
    if (match.max_redemptions != null && match.redeemed_count >= match.max_redemptions) {
      setCodeError("This code has been fully redeemed.");
      return;
    }
    if (match.plan_id && match.plan_id !== plan.id) {
      setCodeError("This code doesn't apply to this plan.");
      return;
    }
    setAppliedCode(match);
    setCodeError("");
  };

  const byType = (["qr", "bank", "wallet"] as const).map((type) => ({
    type,
    methods: methods.filter((m) => m.type === type),
  }));
  const availableTypes = byType.filter((g) => g.methods.length > 0);
  const subList = methodType ? (byType.find((g) => g.type === methodType)?.methods ?? []) : [];
  const method = subList.find((m) => m.id === subMethodId) ?? subList[0] ?? null;

  const uploadProof = async (file: File) => {
    setUploading(true);
    setUploadErr("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(syncApiUrl("/uploads/payment-proof"), {
        method: "POST",
        headers: getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {},
        body: formData,
      });
      if (!res.ok) throw new Error();
      const { key } = (await res.json()) as { key: string };
      setProofKey(key);
      const viewRes = await fetch(syncApiUrl(`/uploads/payment-proof?key=${encodeURIComponent(key)}`), {
        headers: getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {},
      });
      if (viewRes.ok) {
        const { url } = (await viewRes.json()) as { url: string };
        setProofPreviewUrl(url);
      }
    } catch {
      setUploadErr("Couldn't upload screenshot — try again.");
    } finally {
      setUploading(false);
    }
  };

  const save = () => {
    if (!method || !businessId || !proofKey) return;
    const now = new Date().toISOString();
    const periodEnd = new Date();
    periodEnd.setMonth(periodEnd.getMonth() + cycle.months);
    subscriptionPaymentsCollection.insert({
      id: crypto.randomUUID(),
      reference: "PAY-" + Date.now().toString(36).toUpperCase(),
      business_id: businessId,
      submitted_by_user_id: userId,
      plan_id: plan.id,
      billing_cycle_id: cycle.id,
      amount: amount,
      payment_method_type: method.type,
      payment_method_name: method.name,
      proof_data: proofKey,
      txid: txid.trim() || null,
      notes: notes.trim() || null,
      status: "pending",
      reviewed_by_admin_id: null,
      reviewed_at: null,
      reject_reason: null,
      period_start: now,
      period_end: periodEnd.toISOString(),
      parent_payment_id: null,
      resubmission_count: 0,
      discount: totalOff,
      discount_code_id: appliedCode?.id ?? null,
      submitted_at: now,
      created_at: now,
      updated_at: now,
    });
    onClose();
  };

  const summary = (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: colors.textMuted, fontWeight: 700 }}>
        {plan.name} · {cycle.label} ·{" "}
        {totalOff > 0 ? (
          <>
            <span style={{ textDecoration: "line-through", color: colors.textFaint }}>{money(baseAmount)}</span>{" "}
            <span style={{ color: colors.text }}>{money(amount)}</span>
          </>
        ) : (
          <span style={{ color: colors.text }}>{money(amount)}</span>
        )}
      </div>
      {standingOff > 0 && (
        <div style={{ fontSize: 11.5, color: colors.success, fontWeight: 700, marginTop: 3 }}>
          {standingDiscount?.label || "Discount"} · −{money(standingOff)}
        </div>
      )}
      {codeOff > 0 && (
        <div style={{ fontSize: 11.5, color: colors.success, fontWeight: 700, marginTop: 3 }}>
          Code {appliedCode?.code} · −{money(codeOff)}
        </div>
      )}
    </div>
  );

  if (step === "method") {
    return (
      <Modal title="Choose a payment method" onClose={onClose} footer={<Button variant="secondary" onClick={onClose}>Cancel</Button>}>
        {summary}
        {appliedCode ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 12.5, fontWeight: 700, color: colors.success }}>
            <span className="ms" style={{ fontSize: 17 }}>
              check_circle
            </span>
            Code {appliedCode.code} applied
            <button
              onClick={() => {
                setAppliedCode(null);
                setCodeInput("");
              }}
              style={{ border: "none", background: "none", color: colors.textMuted, fontSize: 12, fontWeight: 700, cursor: "pointer", marginLeft: "auto" }}
            >
              Remove
            </button>
          </div>
        ) : (
          <Field label="Have a discount code?">
            <div style={{ display: "flex", gap: 8 }}>
              <TextInput
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value.toUpperCase());
                  setCodeError("");
                }}
                placeholder="e.g. WELCOME20"
                style={{ flex: 1 }}
              />
              <Button variant="secondary" onClick={() => void applyCode()} disabled={!codeInput.trim()}>
                Apply
              </Button>
            </div>
            {codeError && <div style={{ color: "#DC2D49", fontSize: 12, fontWeight: 700, marginTop: 6 }}>{codeError}</div>}
          </Field>
        )}
        {availableTypes.length === 0 ? (
          <div style={{ fontSize: 12.5, color: colors.textMuted }}>
            No payment channels are configured on the platform yet — contact CliqSuite support.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {availableTypes.map((g) => {
              const meta = TYPE_META[g.type];
              return (
                <div
                  key={g.type}
                  onClick={() => {
                    setMethodType(g.type);
                    setSubMethodId(g.methods[0]?.id ?? "");
                    setStep("pay");
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "14px 16px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 14,
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: meta.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "0 0 auto",
                    }}
                  >
                    <span className="ms" style={{ fontSize: 22, color: meta.iconColor }}>
                      {meta.icon}
                    </span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 800 }}>{meta.name}</div>
                    <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>
                      {g.methods.map((m) => m.name).join(" · ")}
                    </div>
                  </div>
                  <span className="ms" style={{ fontSize: 22, color: colors.textFaint }}>
                    chevron_right
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title={methodType ? TYPE_META[methodType].flowTitle : "Pay"}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={() => setStep("method")}>
            Back
          </Button>
          <Button onClick={save} disabled={!method || !proofKey}>
            Submit for verification
          </Button>
        </>
      }
    >
      {summary}
      {subList.length > 1 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {subList.map((m) => (
            <div
              key={m.id}
              onClick={() => setSubMethodId(m.id)}
              style={{
                padding: "7px 13px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer",
                border: m.id === method?.id ? `1.5px solid ${colors.accent}` : `1.5px solid ${colors.border}`,
                background: m.id === method?.id ? "#FFF6F8" : "#fff",
                color: m.id === method?.id ? colors.accent : colors.textMuted,
              }}
            >
              {m.name}
            </div>
          ))}
        </div>
      )}

      {method?.type === "qr" && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 16 }}>
          {method.image_data ? (
            <img
              src={method.image_data}
              alt={method.name}
              style={{ width: 200, height: 200, objectFit: "contain", border: `1px solid ${colors.border}`, borderRadius: 14, background: "#F8F9FA" }}
            />
          ) : (
            <div
              style={{
                width: 200,
                height: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px dashed ${colors.border}`,
                borderRadius: 14,
                color: colors.textFaint,
                fontSize: 12.5,
                fontWeight: 700,
                textAlign: "center",
                padding: 16,
              }}
            >
              No QR image uploaded for {method.name} yet
            </div>
          )}
        </div>
      )}

      {method?.type === "bank" && (
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: 14, padding: "4px 16px", marginBottom: 16 }}>
          {[
            { label: "Bank", value: method.bank_name },
            { label: "Account name", value: method.account_name },
            { label: "Account number", value: method.account_number, canCopy: true },
            { label: "Branch", value: method.branch },
          ].map((row) => (
            <div
              key={row.label}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 0", borderBottom: `1px solid ${colors.border}` }}
            >
              <div style={{ fontSize: 11.5, fontWeight: 800, color: colors.textFaint, width: 110, flex: "0 0 auto" }}>{row.label}</div>
              <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{row.value || "—"}</div>
              {row.canCopy && row.value && (
                <IconButton icon="content_copy" onClick={() => copyToClipboard(row.value!)} />
              )}
            </div>
          ))}
        </div>
      )}

      {method?.type === "wallet" && (
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: 14, padding: "4px 16px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 0", borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: colors.textFaint, width: 110, flex: "0 0 auto" }}>Network</div>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{method.network || "—"}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 0" }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, color: colors.textFaint, width: 110, flex: "0 0 auto" }}>Address</div>
            <div className="mono" style={{ flex: 1, fontSize: 12.5, fontWeight: 700, wordBreak: "break-all" }}>
              {method.wallet_address || "—"}
            </div>
            {method.wallet_address && <IconButton icon="content_copy" onClick={() => copyToClipboard(method.wallet_address!)} />}
          </div>
        </div>
      )}

      {method?.instructions && (
        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 16, lineHeight: 1.5 }}>{method.instructions}</div>
      )}

      <Field label="Payment screenshot (required)">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {proofPreviewUrl && (
            <img
              src={proofPreviewUrl}
              alt=""
              style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", border: `1px solid ${colors.border}`, flex: "0 0 auto" }}
            />
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadProof(file);
              e.target.value = "";
            }}
          />
          <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? "Uploading…" : proofKey ? "Replace screenshot" : "Attach screenshot"}
          </Button>
          {proofKey && (
            <Button
              variant="secondary"
              onClick={() => {
                setProofKey(null);
                setProofPreviewUrl(null);
              }}
              disabled={uploading}
            >
              Remove
            </Button>
          )}
        </div>
        {uploadErr && <div style={{ color: "#DC2D49", fontSize: 12.5, fontWeight: 700, marginTop: 8 }}>{uploadErr}</div>}
      </Field>
      <Field label="Transaction ID / reference (optional)">
        <TextInput value={txid} onChange={(e) => setTxid(e.target.value)} placeholder="e.g. bank transfer ref" />
      </Field>
      <Field label="Notes (optional)">
        <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the reviewer should know" />
      </Field>
    </Modal>
  );
}
